import { z } from "zod";
import weekDataJson from "cldr-core/supplemental/weekData.json";
import { canonicaliseLocale, getBrowserLocale } from "./widgetLocale";

/** Weekday header labels for one calendar column, in the three widths the calendar uses. */
export interface WeekdayLabels {
  /** Single-character label (e.g. "M", "日"). */
  narrow: string;
  /** Abbreviated label (e.g. "Mon", "月"). */
  short: string;
  /** Full weekday name, used as the column's accessible name. */
  long: string;
}

/** Month and year heading labels, in the two widths the calendar uses. */
export interface MonthLabels {
  /** Abbreviated month with the year (e.g. "Sept 2026"). */
  short: string;
  /** Full month with the year (e.g. "September 2026"), used as the accessible name. */
  long: string;
}

/** A weekday, uppercase, ordered Monday→Sunday like CLDR and ISO-8601. */
export type Weekday = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";

/** Weekdays Monday→Sunday; the calendar rotates this list to start on its first day. */
export const WEEKDAYS: readonly Weekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/** Maps a `Weekday` to the JS `Date.getDay()` value (0 = Sunday). */
export const JS_DAY: Record<Weekday, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const cldrWeekdaySchema = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
const weekDataSchema = z.object({
  supplemental: z.object({
    weekData: z.object({
      firstDay: z.record(z.string(), cldrWeekdaySchema),
    }),
  }),
});
type CldrWeekday = z.infer<typeof cldrWeekdaySchema>;

const CLDR_TO_WEEKDAY: Record<CldrWeekday, Weekday> = {
  mon: "MON",
  tue: "TUE",
  wed: "WED",
  thu: "THU",
  fri: "FRI",
  sat: "SAT",
  sun: "SUN",
};

const FIRST_DAY_BY_REGION = weekDataSchema.parse(weekDataJson).supplemental.weekData.firstDay;

/** A Monday, used to generate weekday names without depending on the current date. */
const REFERENCE_MONDAY_UTC = Date.UTC(2024, 0, 1);
const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * The locale the calendar formats against: the supplied locale when `Intl`
 * accepts it, otherwise the browser's own locale. `undefined` falls back to the
 * runtime default.
 */
export function resolveCalendarLocale(locale?: string): string | undefined {
  return canonicaliseLocale(locale) ?? getBrowserLocale();
}

/** The region subtag of the browser's locale (e.g. "GB" for en-GB), or `undefined` when it has none. */
function getBrowserRegion(): string | undefined {
  const locale = getBrowserLocale();
  if (!locale) return undefined;
  try {
    return new Intl.Locale(locale).region;
  } catch {
    return undefined;
  }
}

/**
 * The first day of the calendar week.
 *
 * Resolution chain: the browser locale's explicit region subtag → the venue's
 * country code → CLDR's world default ("001", Monday). A bare browser language
 * like `en` carries no region and falls through to the venue's; `Intl.Locale`
 * is never maximised, so region guesses like en→US are avoided.
 */
export function getFirstDayOfWeek(venueCountryCode?: string | null): Weekday {
  const venueRegion = venueCountryCode?.trim().toUpperCase();
  const region = getBrowserRegion() ?? (venueRegion && /^[A-Z]{2}$/.test(venueRegion) ? venueRegion : undefined);

  const cldrDay = (region ? FIRST_DAY_BY_REGION[region] : undefined) ?? FIRST_DAY_BY_REGION["001"];
  return cldrDay ? CLDR_TO_WEEKDAY[cldrDay] : "MON";
}

/**
 * Weekday header labels in the locale's own language, ordered from the
 * calendar's first day of the week.
 */
export function getWeekdayLabels(firstDay: Weekday, locale?: string): WeekdayLabels[] {
  const resolved = resolveCalendarLocale(locale);
  const narrow = new Intl.DateTimeFormat(resolved, { weekday: "narrow", timeZone: "UTC" });
  const short = new Intl.DateTimeFormat(resolved, { weekday: "short", timeZone: "UTC" });
  const long = new Intl.DateTimeFormat(resolved, { weekday: "long", timeZone: "UTC" });

  // Monday-relative offsets, rotated so the locale's first day leads.
  const startIndex = WEEKDAYS.indexOf(firstDay);
  const offsets = WEEKDAYS.map((_, index) => (startIndex + index) % WEEKDAYS.length);

  return offsets.map((offset) => {
    const date = new Date(REFERENCE_MONDAY_UTC + offset * MILLISECONDS_PER_DAY);
    return {
      narrow: narrow.format(date),
      short: short.format(date),
      long: long.format(date),
    };
  });
}

/**
 * The calendar's month and year heading in the locale's own language, for a
 * zero-indexed month.
 */
export function getMonthLabels(year: number, month: number, locale?: string): MonthLabels {
  const resolved = resolveCalendarLocale(locale);
  const date = new Date(Date.UTC(year, month, 1));
  const format = (monthWidth: "short" | "long"): string =>
    new Intl.DateTimeFormat(resolved, { month: monthWidth, year: "numeric", timeZone: "UTC" }).format(date);

  return { short: format("short"), long: format("long") };
}
